import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { getEventStatus } from 'app/shared/utils/calcEventState';
import { EventStatus } from 'app/shared/utils/events';
import {
  AttendeeState,
  Event,
  EventForCompactEventCard,
  Tag,
} from 'app/shared/typings';

function filterByVisibleToFans(tag: Tag) {
  return !!tag.isVisibleToFans;
}

function getVenueCategoryTag(event: EventForCompactEventCard | Event) {
  const { venue } = event;
  const venueCategories = venue?.venueCategories;
  const visibleCategories: Tag[] | undefined = venueCategories?.filter(
    filterByVisibleToFans
  );

  if (!visibleCategories || !visibleCategories[0]) {
    return;
  }

  return visibleCategories[0];
}

export function shouldDisplaySoldOutEventTag(
  event: EventForCompactEventCard,
  userAttendeeState: AttendeeState | undefined,
  cardDisplaysAttendeeStatusMessage: boolean
) {
  if (cardDisplaysAttendeeStatusMessage) {
    return false;
  }

  const eventStatus = getEventStatus(event, userAttendeeState);

  return eventStatus !== EventStatus.NONE;
}

export function getEventVenueTagForKlaviyo(event: Event) {
  if (!event) {
    return;
  }

  if (event.theme) {
    return event.theme.title;
  }

  if (event.isVenueConfirmed && !!event.venue) {
    const venueCategoryTag = getVenueCategoryTag(event);

    if (venueCategoryTag) {
      return venueCategoryTag.name;
    }
  }

  return;
}

export const useGetEventTagsProps = () => {
  const { formatMessage } = useIntl();
  const { colors } = useCurrentTheme();

  const defaultProps = {
    b: colors.accent.primaryAccent.base,
    color: colors.primary.black.base,
  };

  const getEventTagsProps = useCallback(
    (
      event: EventForCompactEventCard,
      userAttendeeState: AttendeeState | undefined,
      cardDisplaysAttendeeStatusMessage: boolean = false
    ): any[] => {
      if (
        shouldDisplaySoldOutEventTag(
          event,
          userAttendeeState,
          cardDisplaysAttendeeStatusMessage
        )
      ) {
        return [
          {
            tag: formatMessage({ id: 'shared.soldOut' }),
            backgroundColor: colors.messaging.error.dark,
            b: colors.messaging.error.dark,
            color: colors.primary.white.base,
          },
        ];
      }

      if (event.onPresale) {
        return [
          {
            tag: formatMessage({ id: 'shared.visaPresale' }),
            ...defaultProps,
          },
        ];
      }

      if (event.theme) {
        return [{ tag: event.theme.title, ...defaultProps }];
      }

      if (event.isVenueConfirmed && !!event.venue) {
        const venueCategoryTag = getVenueCategoryTag(event);

        if (venueCategoryTag) {
          return [{ tag: venueCategoryTag.name, ...defaultProps }];
        }
      }

      return [];
    },
    []
  );

  return getEventTagsProps;
};
